import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { useTheme } from '../../ThemeContext';
import { supabase } from '../../supabaseClient';

const UserSettings = () => {
  const { user } = useContext(AuthContext);
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  useEffect(() => {
    let ignore = false;

    const fetchUserDataWrapper = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user?.id) {
          throw new Error('Geen gebruiker ID gevonden');
        }

        let profileData;
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, email, theme_preference')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (!data) {
          // Als er geen profiel bestaat, maak er dan een aan
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              email: user.email,
              username: user.email?.split('@')[0] || '',
              full_name: '',
              theme_preference: 'light',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (createError) throw createError;
          profileData = newProfile;
        } else {
          profileData = data;
        }

        // Set the theme if it exists in the profile
        if (profileData.theme_preference) {
          setMode(profileData.theme_preference);
        }

        setFormData({
          username: data.username || '',
          fullName: data.full_name || '',
          email: data.email || user.email || ''
        });
      } catch (error) {
        setError(`Fout bij het ophalen van gebruikersgegevens: ${error.message}`);
        setFormData({
          username: user?.email?.split('@')[0] || '',
          fullName: '',
          email: user?.email || ''
        });
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchUserDataWrapper();

    return () => {
      ignore = true;
    };
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setSuccess('Gebruikersgegevens zijn succesvol bijgewerkt');
    } catch (error) {
      setError(`Fout bij het bijwerken van gebruikersgegevens: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setPasswordError(null);
      setPasswordSuccess(null);

      // Validaties
      if (!passwordData.currentPassword) {
        throw new Error('Vul je huidige wachtwoord in');
      }

      if (!passwordData.newPassword) {
        throw new Error('Vul een nieuw wachtwoord in');
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('Nieuwe wachtwoorden komen niet overeen');
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error('Wachtwoord moet minimaal 6 karakters lang zijn');
      }

      // Eerst inloggen met het huidige wachtwoord
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword,
      });

      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          throw new Error('Het huidige wachtwoord is onjuist');
        }
        throw new Error(`Inloggen mislukt: ${signInError.message}`);
      }

      // Dan het nieuwe wachtwoord instellen
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (updateError) {
        throw new Error(`Wachtwoord wijzigen mislukt: ${updateError.message}`);
      }

      setPasswordSuccess('Je wachtwoord is succesvol gewijzigd! Je wordt nu uitgelogd...');
      
      // Reset password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Wacht 2 seconden voordat we uitloggen
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login', { 
          state: { 
            message: 'Wachtwoord succesvol gewijzigd. Log opnieuw in met je nieuwe wachtwoord.' 
          } 
        });
      }, 2000);

    } catch (error) {
      setPasswordError(error.message);
      
      // Toon error message voor 5 seconden
      setTimeout(() => {
        setPasswordError(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      fullName: value
    }));
  };

  if (loading && !formData.email) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleThemeChange = async (event, newMode) => {
    if (newMode !== null) {
      try {
        setLoading(true);
        // Update the theme in the database
        const { error } = await supabase
          .from('profiles')
          .update({
            theme_preference: newMode,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (error) throw error;

        // If database update successful, update the local theme state
        setMode(newMode);
      } catch (error) {
        setError(`Fout bij het opslaan van thema voorkeur: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Gebruikersinstellingen
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Volledige naam"
            name="full_name"
            value={formData.fullName || ''}
            onChange={handleNameChange}
            margin="normal"
            inputProps={{ 
              autoComplete: "off",
              spellCheck: "false"
            }}
          />

          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            disabled
            margin="normal"
          />

          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </Box>
        </form>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Thema Instellingen
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleThemeChange}
          aria-label="thema selectie"
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="light" aria-label="licht thema">
            Licht
          </ToggleButton>
          <ToggleButton value="dark" aria-label="donker thema">
            Donker
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 3 }}>
          Wachtwoord Wijzigen
        </Typography>

        {passwordError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {passwordError}
          </Alert>
        )}

        {passwordSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {passwordSuccess}
          </Alert>
        )}

        <form onSubmit={handlePasswordChange}>
          {/* Hidden email field for password managers */}
          <TextField
            name="email" // Name attribute is important
            type="email" // Use type email
            autoComplete="username" // Standard autocomplete value for username/email
            value={user?.email || ''} // Get email from context
            sx={{ position: 'absolute', left: '-9999px' }} // Visually hide the field
            tabIndex={-1} // Prevent tabbing to the hidden field
          />

          <TextField
            fullWidth
            label="Huidig wachtwoord"
            type={showPasswords.current ? 'text' : 'password'}
            name="currentPassword"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData(prev => ({
              ...prev,
              currentPassword: e.target.value
            }))}
            required
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle current password visibility"
                    onClick={() => togglePasswordVisibility('current')}
                    edge="end"
                  >
                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Nieuw wachtwoord"
            type={showPasswords.new ? 'text' : 'password'}
            name="newPassword"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData(prev => ({
              ...prev,
              newPassword: e.target.value
            }))}
            required
            margin="normal"
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => togglePasswordVisibility('new')}
                    edge="end"
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Bevestig nieuw wachtwoord"
            type={showPasswords.confirm ? 'text' : 'password'}
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData(prev => ({
              ...prev,
              confirmPassword: e.target.value
            }))}
            required
            margin="normal"
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => togglePasswordVisibility('confirm')}
                    edge="end"
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            >
              {loading ? 'Wachtwoord Wijzigen...' : 'Wachtwoord Wijzigen'}
            </Button>
          </Box>
        </form>

        <Divider sx={{ my: 4 }} />

        <Typography variant="subtitle2" color="textSecondary">
          Account ID: {user?.id}
        </Typography>
      </Paper>
    </Box>
  );
};

export default UserSettings;
